import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useLocation } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { RequisicaoCompra } from "../../domain/models/requisicao_compra";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { initialRequisicaoCompraForm, RequisicaoCompraForm, RequisicaoCompraSchema } from "../schemas/RequisicaoServicosProdutos_cadastro/requisicao_compra";
import { useCentroCustoDrawer } from "../../../../common/components/drawers_api/drawers/centro_custo_drawer";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { FuncionarioInformation } from "../../../../common/domain/models/funcionario_information";
import { useRequisicaoServicosProdutosPagination } from "../hooks/RequisicaoServicosProdutos_cadastro/pagination";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { RequisicaoProdutoServicoLista } from "../../domain/models/requisicao_produto_servico_lista";
import { AlteracaoRequisicaoProdutoServico } from "../../domain/models/alteracao_requisicao_produto_servico";
import { RequisicaoProdutoServicoForm } from "../schemas/RequisicaoServicosProdutos_cadastro/requisicao_produto_servico";
import { ProdutoServicoDropDown } from "../../../../common/domain/models/produto_servico_dropdown";
import { AtributoCompra } from "../../../../common/domain/models/atributo_compra";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { TelasIds } from "../../../../common/utils/tela_ids";
import { useProdutoServicoDrawer } from "../../../../common/components/drawers_api/drawers/produto_servico_drawer";

import dayjs from "dayjs";
import { SubcontaDropdown } from "../../../rh/domain/models/subconta_dropdown";

/**
 * LoadRequisicaoProdutosServicosFuncState
 */

type LoadRequisicaoProdutosServicosFuncState = {
  idRequisicaoCompra?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useRequisicaoServicosProdutosCadastroViewModel
 */

export const useRequisicaoServicosProdutosCadastroViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase,
      getFuncionarioByIdUsuarioUseCase,
      getAtributosCompraUseCase,
    },
    compras: {
      getRequisicaoCompraByIdUseCase,
      createRequisicaoCompraUseCase,
      updateRequisicaoCompraUseCase,
      deleteRequisicaoCompraUseCase,
      getAllRequisicaoProdutoServicoUseCase,
      getAllAlteracaoRequisicaoCompraProdutoServicoUseCase,
      createRequisicaoCompraProdutoServicoUseCase,
      updateRequisicaoCompraProdutoServicoUseCase,
      deleteRequisicaoCompraProdutoServicoUseCase,
      createServicoProdutoUseCase,
      createColetaPrecoUseCase,
      downloadRequisicaoCompraArquivoUseCase,
    },
    comercial: {
      createCentroCustoUseCase,
    },
    reports: {
      getReportRequisicaoCompraUseCase,
    }
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadProdutoServicosDropDown,
    loadSubContasDropDown,
  } = useDropDownLoader();

  const {
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
    setCustomLoading,
  } = usePageLoader();

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();

  const { idUsuario } = useUserAuthStore();
  const [funcionarioInfo, setFuncionarioInfo] = useState<FuncionarioInformation>();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();
  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>();
  const [produtosServicosDropdown, setProdutosServicosDropdown] = useState<ProdutoServicoDropDown[]>();
  const [subcontasDropdown, setSubcontasDropdown] = useState<SubcontaDropdown[]>();

  const [idRequisicaoCompra, setIdRequisicaoProdutoServico] = useState<number>();
  const [requisicaoCompra, setRequisicaoCompra] = useState<RequisicaoCompra>();

  const [atributoCompra, setAtributoCompra] = useState<AtributoCompra>();

  const [requisicaoProdutosServicos, setRequisicaoProdutosServicos] = useState<RequisicaoProdutoServicoLista[]>();
  const [alteracaoRequisicaoProdutosServicos, setAlteracaoRequisicaoProdutosServicos] = useState<AlteracaoRequisicaoProdutoServico[]>();
  const [selectedRequisicaoProdutoServico, setSelectedRequisicaoProdutoServico] = useState<RequisicaoProdutoServicoLista>();

  const [requisicaoProdutoServicoModal, setRequisicaoProdutoServicoModal] = useState(false);
  const [requisicaoProdutoServicoDeleteModal, setRequisicaoProdutoServicoDeleteModal] = useState(false);
  const [requisicaoCompraDeleteModal, setRequisicaoCompraDeleteModal] = useState(false);

  const [enableColetaPreco, setEnableColetaPreco] = useState(false);

  const [lockEdition, setLockEdition] = useState(false);

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: e => pushErrorNotification("Erro", [e]),
  });

  const buttonOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Novo',
      },
      {
        id: 2,
        label: 'Relatório',
      },
      {
        id: 2,
        label: 'Excluir',
      },
    ];
  }, [
  ]);

  const lockSolicitante = useMemo(() => {
    if (requisicaoCompra !== undefined) {
      if (requisicaoCompra.idFuncionario !== null) {
        if (funcionarioInfo !== undefined && funcionarioInfo.idFuncionario === requisicaoCompra.idFuncionario) {
          return true;
        }
      }
    }
    return false;
  }, [
    funcionarioInfo,
    requisicaoCompra,
  ]);

  const [centroCustoDrawerState, startCentroCustoDrawer] = useCentroCustoDrawer();
  const [produtoServicoDrawerState, startProdutoServicoDrawer] = useProdutoServicoDrawer();

  const hookForm = useForm<RequisicaoCompraForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { status: 0, nro: undefined },
    resolver: zodResolver(RequisicaoCompraSchema),
  });

  const pagination = useRequisicaoServicosProdutosPagination();

  const [status, aprovadoPor, dataAprovadoPor] = hookForm.watch(['status', 'aprovadoPor', 'dataAprovadoPor']);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Pendente',
        disabled: status === 5,
        style: {
          backgroundColor: '#c6c6c6',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.75rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Aprovada',
        disabled: status === 5,
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.75rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 2,
        label: 'Reprovada',
        disabled: status === 5,
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.75rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 3,
        label: 'Finalizada',
        disabled: status === 0 || (status === 1 &&
          (aprovadoPor === undefined || aprovadoPor === null ||
            dataAprovadoPor === undefined || dataAprovadoPor === null)
        ),
        style: {
          backgroundColor: '#428bca',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.75rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 4,
        label: 'Revisar',
        disabled: status === 5,
        style: {
          backgroundColor: '#89c4f4',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.75rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 5,
        label: 'Coleta Iniciada',
        disabled: true,
        style: {
          backgroundColor: '#c6c6c6',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.75rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
    status,
    aprovadoPor,
    dataAprovadoPor,
  ]);

  const funcionariosAprovadosDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => {
      if (requisicaoCompra) {
        if (x.idFuncionario === requisicaoCompra.idFuncionarioAprovacao) {
          return true;
        }
      }
      return x.canBeSelected === true;
    });
  }, [
    requisicaoCompra,
    funcionariosDropdown,
  ]);

  const funcionariosSolicitanteDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => {
      if (requisicaoCompra) {
        if (x.idFuncionario === requisicaoCompra.idFuncionario) {
          return true;
        }
      }
      return x.canBeSelected === true;
    });
  }, [
    requisicaoCompra,
    funcionariosDropdown,
  ]);

  /**
   * Funções de carregamento
   */

  const loadAtributoCompra = useCallback(async () => {
    try {
      const atributoCompra = await getAtributosCompraUseCase.getAtributosCompra(null);
      if (atributoCompra !== null) {
        changeHistory.updateShowDateColumn(atributoCompra.exibirDataRequisicao ?? true);
      }
      setAtributoCompra(atributoCompra ?? undefined);
    } catch (e: unknown) {
      setAtributoCompra(undefined);
      throw new Error('Falha ao buscar atributos de compras!');
    }
  }, [
    changeHistory,
    getAtributosCompraUseCase
  ]);

  const loadFuncionarioInfo = useCallback(async () => {
    if (idUsuario) {
      try {
        const userInfo = await getFuncionarioByIdUsuarioUseCase
          .getFuncionarioByIdUsuario(idUsuario);
        setFuncionarioInfo(userInfo ?? undefined);
        return userInfo;
      } catch (e: unknown) {
        setFuncionarioInfo(undefined);
        throw new Error('Falha ao buscar dados do usuário!');
      }
    }
    return null;
  }, [
    idUsuario,
    getFuncionarioByIdUsuarioUseCase,
  ]);

  const loadRequisicao = useCallback(async (id?: number) => {
    try {
      const filteredIdRequisicaoCompra = id ?? idRequisicaoCompra;
      const response = await getRequisicaoCompraByIdUseCase
        .getRequisicaoCompraById(filteredIdRequisicaoCompra);

      requestAnimationFrame(() => {
        hookForm.reset();

        hookForm.setValue('status', response.status ?? 0);
        hookForm.setValue('nro', response.nrRequisicaoCompra ?? undefined);

        setLockEdition(response.status === 1 || response.status === 2 || response.status === 3 || response.status === 5);
        setEnableColetaPreco(response.status === 1);

        if (response.dataCadastro) {
          hookForm.setValue('dataCadastro', dayjs.utc(response.dataCadastro).local().toDate());
        } else {
          hookForm.setValue('dataCadastro', dayjs().local().toDate());
        }

        if (response.idFuncionario) {
          hookForm.setValue('solicitante', response.idFuncionario);
        } else {
          hookForm.resetField('solicitante');
        }

        if (response.dataRequisicao) {
          hookForm.setValue('dataRequisicao', dayjs.utc(response.dataRequisicao).local().toDate());
        } else {
          hookForm.resetField('dataRequisicao');
        }

        if (response.idCentroCusto) {
          hookForm.setValue('centroCusto', response.idCentroCusto);
        } else {
          hookForm.resetField('centroCusto');
        }

        if (response.dataPrevista) {
          hookForm.setValue('dataPrevistaUtilizacao', dayjs.utc(response.dataPrevista).local().toDate());
        } else {
          hookForm.resetField('dataPrevistaUtilizacao');
        }

        if (response.idFuncionarioAprovacao) {
          hookForm.setValue('aprovadoPor', response.idFuncionarioAprovacao);
        } else {
          hookForm.resetField('aprovadoPor');
        }

        if (response.dataAprovacao) {
          hookForm.setValue('dataAprovadoPor', dayjs.utc(response.dataAprovacao).local().toDate());
        } else {
          hookForm.resetField('dataAprovadoPor', { defaultValue: null });
        }

        hookForm.setValue('descricao', response.descricao ?? '');
        hookForm.setValue('observacao', response.observacoes ?? '');
      });

      setRequisicaoCompra(response);
    } catch (e: unknown) {
      setRequisicaoCompra(undefined);
      throw new Error('Falha ao buscar dados da página!');
    }
  }, [
    hookForm,
    idRequisicaoCompra,
    getRequisicaoCompraByIdUseCase,
  ]);

  const loadRequisicaoProdutosServicos = useCallback(async (fs?: LoadRequisicaoProdutosServicosFuncState) => {
    try {
      const filteredIdRequisicaoCompra = fs?.idRequisicaoCompra ?? idRequisicaoCompra;
      const filteredPage = fs?.page ?? pagination.requisicaoProdutosServicos.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.requisicaoProdutosServicos.rowsPerPage;

      await getAllRequisicaoProdutoServicoUseCase.getAllRequisicaoProdutoServico(
        filteredIdRequisicaoCompra,
        filteredPage,
        filteredRowsPerPage,
      );

      setRequisicaoProdutosServicos(getAllRequisicaoProdutoServicoUseCase.items);
      pagination.requisicaoProdutosServicosSetter.setTotalItems(getAllRequisicaoProdutoServicoUseCase.totalItems);
    } catch (e: unknown) {
      setRequisicaoProdutosServicos(undefined);
      throw new Error('Falha ao buscar requisições de produtos e serviços!');
    }
  }, [
    idRequisicaoCompra,
    pagination,
    getAllRequisicaoProdutoServicoUseCase,
  ]);

  const loadAlteracaoRequisicaoProdutosServicos = useCallback(async (fs?: LoadRequisicaoProdutosServicosFuncState) => {
    try {
      const filteredIdRequisicaoCompra = fs?.idRequisicaoCompra ?? idRequisicaoCompra;
      const filteredPage = fs?.page ?? pagination.alteracaoRequisicaoProdutosServicos.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.alteracaoRequisicaoProdutosServicos.rowsPerPage;

      await getAllAlteracaoRequisicaoCompraProdutoServicoUseCase.getAllAlteracaoRequisicaoCompraProdutoServico(
        filteredIdRequisicaoCompra,
        filteredPage,
        filteredRowsPerPage,
      );

      setAlteracaoRequisicaoProdutosServicos(getAllAlteracaoRequisicaoCompraProdutoServicoUseCase.items);
      pagination.alteracaoRequisicaoProdutosServicosSetter.setTotalItems(getAllAlteracaoRequisicaoCompraProdutoServicoUseCase.totalItems);
    } catch (e: unknown) {
      setAlteracaoRequisicaoProdutosServicos(undefined);
      throw new Error('Falha ao buscar alterações de requisições de produtos e serviços!');
    }
  }, [
    idRequisicaoCompra,
    pagination,
    getAllAlteracaoRequisicaoCompraProdutoServicoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationIdRequisicao = params.get('id');

      const info = await loadFuncionarioInfo();

      await loadAtributoCompra();

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadProdutoServicosDropDown(dt => setProdutosServicosDropdown(dt), {});

      await loadSubContasDropDown(dt => setSubcontasDropdown(dt));

      if (locationIdRequisicao && locationIdRequisicao.length > 0) {
        const idRequisicao = Number(locationIdRequisicao);
        setIdRequisicaoProdutoServico(idRequisicao);
        await loadRequisicao(idRequisicao);

        useRequisicaoServicosProdutosPagination.getState().requisicaoProdutosServicosSetter.setPage(1);
        useRequisicaoServicosProdutosPagination.getState().alteracaoRequisicaoProdutosServicosSetter.setPage(1);

        await loadRequisicaoProdutosServicos({ idRequisicaoCompra: idRequisicao, page: 1 });
        await loadAlteracaoRequisicaoProdutosServicos({ idRequisicaoCompra: idRequisicao, page: 1 });

      } else {
        setLockEdition(false);

        hookForm.reset({ ...initialRequisicaoCompraForm() });
        hookForm.setValue('dataCadastro', dayjs().local().toDate());

        if (info && info.idFuncionario) {
          hookForm.setValue('solicitante', info.idFuncionario);
        }
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      const info = await loadFuncionarioInfo();

      await loadAtributoCompra();

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadProdutoServicosDropDown(dt => setProdutosServicosDropdown(dt), {});

      await loadSubContasDropDown(dt => setSubcontasDropdown(dt));

      if (idRequisicaoCompra) {
        await loadRequisicao(idRequisicaoCompra);
        await loadRequisicaoProdutosServicos({ idRequisicaoCompra: idRequisicaoCompra });
        await loadAlteracaoRequisicaoProdutosServicos({ idRequisicaoCompra: idRequisicaoCompra });
      } else {
        if (info && info.idFuncionario) {
          hookForm.setValue('solicitante', info.idFuncionario);
        }
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    }
  }, [
    idRequisicaoCompra,
    hookForm,
    loadFuncionarioInfo,
    loadAtributoCompra,
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadProdutoServicosDropDown,
    loadSubContasDropDown,
    loadRequisicao,
    loadRequisicaoProdutosServicos,
    loadAlteracaoRequisicaoProdutosServicos,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/RequisicaoServicosProdutos_lista' });
  }, [
    backOrNavigate,
  ]);

  const handleOptionPopperActions = useCallback(async (option: number) => {
    switch (option) {
      case 1: /* Novo */
        setIdRequisicaoProdutoServico(undefined);

        setRequisicaoCompra(undefined);
        setRequisicaoCompra(undefined);
        setAlteracaoRequisicaoProdutosServicos(undefined);

        const info = await loadFuncionarioInfo();

        await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

        await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

        hookForm.reset({ ...initialRequisicaoCompraForm() });

        if (info && info.idFuncionario) {
          hookForm.setValue('solicitante', info.idFuncionario);
        }

        const newUrl = `${window.location.pathname}`;
        window.history.replaceState({}, "", newUrl);

        break;

      case 2: /* Relatorio */
        if (idRequisicaoCompra) {
          try {
            setLoading(true);
            await startReportViewerProcess({
              fetchCallback: async (requiredMimeType) =>
                await getReportRequisicaoCompraUseCase
                  .getReportRequisicaoCompra(idRequisicaoCompra, requiredMimeType),
            });
          } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao gerar relatorio!'));
          } finally {
            setLoading(false);
          }
        }
        break;

      case 3: /* Excluir */
        if (idRequisicaoCompra) {
          setRequisicaoCompraDeleteModal(true);
        }
        break;
    }
  }, [
    idRequisicaoCompra,
    hookForm,
    getReportRequisicaoCompraUseCase,
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadFuncionarioInfo,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleRequisicaoCompraSubmit = useCallback(async (form: RequisicaoCompraForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (idRequisicaoCompra) {
        if (form.status === 1) {
          if (form.aprovadoPor === null) {
            if (funcionarioInfo && funcionarioInfo.idFuncionario) {
              form.aprovadoPor = funcionarioInfo.idFuncionario;
            }
          }
          if (form.dataAprovadoPor === null) {
            form.dataAprovadoPor = dayjs().local().utc().toDate();
          }
        }

        await updateRequisicaoCompraUseCase.updateRequisicaoCompra(
          idRequisicaoCompra,
          form.status,
          form.aprovadoPor,
          form.dataAprovadoPor,
          form.dataCadastro,
          form.solicitante,
          form.dataRequisicao,
          form.centroCusto,
          form.dataPrevistaUtilizacao,
          form.descricao,
          form.observacao,
          form.arquivo1,
          form.limparArquivo1,
          form.arquivo2,
          form.limparArquivo2,
        );

        await loadRequisicao();
      } else {
        if (form.status === 1) {
          if (form.aprovadoPor === null) {
            if (funcionarioInfo && funcionarioInfo.idFuncionario) {
              form.aprovadoPor = funcionarioInfo.idFuncionario;
            }
          }
          if (form.dataAprovadoPor === null) {
            form.dataAprovadoPor = dayjs().local().utc().toDate();
          }
        }

        const id = await createRequisicaoCompraUseCase.createRequisicaoCompra(
          form.status,
          form.aprovadoPor,
          form.dataAprovadoPor,
          form.dataCadastro,
          form.solicitante,
          form.dataRequisicao,
          form.centroCusto,
          form.dataPrevistaUtilizacao,
          form.descricao,
          form.observacao,
          form.arquivo1,
          form.arquivo2,
        );

        const params = new URLSearchParams(window.location.search);
        params.set("id", id.toString());

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", newUrl);

        setIdRequisicaoProdutoServico(id);
        await loadRequisicao(id);
      }
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      await loadRequisicao();
      pushErrorNotification('Erro', processError(e, 'Falha ao salvar requisição de compra!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    idRequisicaoCompra,
    funcionarioInfo,
    createRequisicaoCompraUseCase,
    updateRequisicaoCompraUseCase,
    loadRequisicao,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleRequisicaoCompraDeleteSubmit = useCallback(async () => {
    if (idRequisicaoCompra) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteRequisicaoCompraUseCase
          .deleteRequisicaoCompra(TelasIds.RequisicaoServicosProdutos_cadastro, idRequisicaoCompra);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');

        backOrNavigate(-1, { alternativeRoute: '/RequisicaoServicosProdutos_lista' });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir registro!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idRequisicaoCompra,
    deleteRequisicaoCompraUseCase,
    pushInfoNotification,
    backOrNavigate,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleRequisicaoProdutoServicoSubmit = useCallback(async (form: RequisicaoProdutoServicoForm) => {
    if (idRequisicaoCompra) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedRequisicaoProdutoServico) {
          await updateRequisicaoCompraProdutoServicoUseCase.updateRequisicaoCompraProdutoServico(
            selectedRequisicaoProdutoServico.idRequisicaoProdutoServico,
            form.idProdutoServico,
            form.idSubconta,
            form.quantidade,
          );
        } else {
          await createRequisicaoCompraProdutoServicoUseCase.createRequisicaoCompraProdutoServico(
            idRequisicaoCompra,
            form.idProdutoServico,
            form.idSubconta,
            form.quantidade,
          );
        }

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

        await loadRequisicaoProdutosServicos();
        await loadAlteracaoRequisicaoProdutosServicos();

        setRequisicaoProdutoServicoModal(false);
        setSelectedRequisicaoProdutoServico(undefined);

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao salvar requisição de produto/serviço!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idRequisicaoCompra,
    selectedRequisicaoProdutoServico,
    createRequisicaoCompraProdutoServicoUseCase,
    updateRequisicaoCompraProdutoServicoUseCase,
    loadRequisicaoProdutosServicos,
    loadAlteracaoRequisicaoProdutosServicos,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleRequisicaoProdutoServicoDeleteSubmit = useCallback(async () => {
    if (selectedRequisicaoProdutoServico) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteRequisicaoCompraProdutoServicoUseCase.deleteRequisicaoCompraProdutoServico(
          selectedRequisicaoProdutoServico.idRequisicaoProdutoServico,
        );

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');

        await loadRequisicaoProdutosServicos();
        await loadAlteracaoRequisicaoProdutosServicos();

        setRequisicaoProdutoServicoDeleteModal(false);
        setSelectedRequisicaoProdutoServico(undefined);

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao salvar requisição de produto/serviço!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedRequisicaoProdutoServico,
    deleteRequisicaoCompraProdutoServicoUseCase,
    loadRequisicaoProdutosServicos,
    loadAlteracaoRequisicaoProdutosServicos,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleNewProdutoServico = useCallback((callback: (idProdutoServico: number) => void) => {
    startProdutoServicoDrawer(
      async (form) => {
        try {
          setSaving(true);
          setPageSaving(true);

          callback(await createServicoProdutoUseCase.createServicoProduto(
            form.ativo,
            form.produtoServico,
            form.tipo,
            form.unidade,
            form.norma,
          ));

          await loadProdutoServicosDropDown(dt => setProdutosServicosDropdown(dt), {});

          pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, 'Falha ao salvar requisição de produto/serviço!'));
        } finally {
          setSaving(false);
          setPageSaving(false);
        }
      }
    );
  }, [
    createServicoProdutoUseCase,
    startProdutoServicoDrawer,
    loadProdutoServicosDropDown,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleStatusChangeSubmit = useCallback(async (newValue: number | null, prevValue: number | null) => {
    if (idRequisicaoCompra && newValue !== null && newValue !== prevValue) {
      if ((newValue === 1 || newValue === 3 || newValue === 5)) {
        if (!funcionarioInfo || !funcionarioInfo.idFuncionario) {
          await loadRequisicao();
          pushErrorNotification('Erro', 'Usuário sem vínculo com funcionário não pode alterar o status da requisição!');
          return;
        }
        hookForm.setValue('aprovadoPor', funcionarioInfo.idFuncionario);
        hookForm.setValue('dataAprovadoPor', dayjs().local().utc().toDate());
      }
      requestAnimationFrame(() => {
        hookForm.handleSubmit(handleRequisicaoCompraSubmit)();
      });
    }
  }, [
    funcionarioInfo,
    idRequisicaoCompra,
    hookForm,
    loadRequisicao,
    handleRequisicaoCompraSubmit,
    pushErrorNotification,
  ]);

  const handleCreateCentroCusto = useCallback(() => {
    startCentroCustoDrawer(
      async (form) => {
        try {
          setSaving(true);
          setPageSaving(true);

          const { idCentroCusto } = await createCentroCustoUseCase.createCentroCusto(
            form.status,
            form.codigo,
            form.descricao,
            form.bdi,
            form.bdi2,
            form.numeroCno ?? undefined,
            form.obsCentroCusto ?? undefined,
          );

          await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

          hookForm.setValue('centroCusto', idCentroCusto);

          pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, 'Falha ao salvar requisição de produto/serviço!'));
        } finally {
          setSaving(false);
          setPageSaving(false);
        }
      }
    );
  }, [
    hookForm,
    createCentroCustoUseCase,
    startCentroCustoDrawer,
    loadCentroCustoDropDown,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleRequisicaoServicoProdutoPageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useRequisicaoServicosProdutosPagination.getState().requisicaoProdutosServicosSetter.setPage(page + 1);
      await loadRequisicaoProdutosServicos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadRequisicaoProdutosServicos,
    pushErrorNotification,
  ]);

  const handleRequisicaoServicoProdutoRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useRequisicaoServicosProdutosPagination.getState().requisicaoProdutosServicosSetter.setPage(1);
      useRequisicaoServicosProdutosPagination.getState().requisicaoProdutosServicosSetter.setRowsPerPage(rowsPerPage);
      await loadRequisicaoProdutosServicos({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadRequisicaoProdutosServicos,
    pushErrorNotification,
  ]);

  const handleAlteracaoRequisicaoServicoProdutoPageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useRequisicaoServicosProdutosPagination.getState().alteracaoRequisicaoProdutosServicosSetter.setPage(page + 1);
      await loadAlteracaoRequisicaoProdutosServicos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadAlteracaoRequisicaoProdutosServicos,
    pushErrorNotification,
  ]);

  const handleAlteracaoRequisicaoServicoProdutoRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useRequisicaoServicosProdutosPagination.getState().alteracaoRequisicaoProdutosServicosSetter.setPage(1);
      useRequisicaoServicosProdutosPagination.getState().alteracaoRequisicaoProdutosServicosSetter.setRowsPerPage(rowsPerPage);
      await loadAlteracaoRequisicaoProdutosServicos({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadAlteracaoRequisicaoProdutosServicos,
    pushErrorNotification,
  ]);

  const handleDeleteFile = useCallback((fileNumber: number) => {
    switch (fileNumber) {
      case 1:
        setRequisicaoCompra(prev => {
          if (prev === undefined) return prev;
          return ({ ...prev, nomearquivo1: null, arquivo1: null });
        });
        break;

      case 2:
        setRequisicaoCompra(prev => {
          if (prev === undefined) return prev;
          return ({ ...prev, nomearquivo2: null, arquivo2: null });
        });
        break;
    }
  }, [
  ]);

  const handleDownloadFile = useCallback(async (nroArquivo: number, nomeArquivo: string) => {
    if (requisicaoCompra) {
      try {
        setCustomLoading(true);

        await downloadRequisicaoCompraArquivoUseCase
          .downloadRequisicaoCompraArquivo(requisicaoCompra.idRequisicaoCompra, nroArquivo, nomeArquivo);

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao baixar arquivo!'));
      } finally {
        setCustomLoading(false);
      }
    }
  }, [
    requisicaoCompra,
    downloadRequisicaoCompraArquivoUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleRequisicaoCompraDeleteOpenState = useCallback((openState: boolean) => {
    setRequisicaoCompraDeleteModal(openState);
  }, [
  ]);

  const handleRequisicaoProdutoServicoOpenState = useCallback((idRequisicaoProdutoServico: number | null, openState: boolean) => {
    if (openState) {
      if (idRequisicaoProdutoServico && requisicaoProdutosServicos) {
        setSelectedRequisicaoProdutoServico(requisicaoProdutosServicos.find(x => x.idRequisicaoProdutoServico === idRequisicaoProdutoServico));
      } else {
        setSelectedRequisicaoProdutoServico(undefined);
      }
    }
    setRequisicaoProdutoServicoModal(openState);
  }, [
    requisicaoProdutosServicos,
  ]);

  const handleProdutoServicoOpenState = useCallback((openState: boolean) => {
    if (idRequisicaoCompra) {
      setRequisicaoProdutoServicoModal(true);
    }
  }, [
    idRequisicaoCompra,
  ]);

  const handleRequisicaoProdutoServicoDeleteOpenState = useCallback((idRequisicaoProdutoServico: number | null, openState: boolean) => {
    if (openState) {
      if (idRequisicaoProdutoServico && requisicaoProdutosServicos) {
        setSelectedRequisicaoProdutoServico(requisicaoProdutosServicos.find(x => x.idRequisicaoProdutoServico === idRequisicaoProdutoServico));
      } else {
        setSelectedRequisicaoProdutoServico(undefined);
      }
    }
    setRequisicaoProdutoServicoDeleteModal(openState);
  }, [
    requisicaoProdutosServicos,
  ]);

  const handleNovaColetaPrecos = useCallback(async (openNewTab: boolean) => {
    try {
      const idFuncionario = funcionarioInfo?.idFuncionario ?? undefined;

      const idColetaPreco = await createColetaPrecoUseCase.createColetaPreco(
        0,
        new Date(),
        idRequisicaoCompra,
        new Date(),
        undefined,
        idFuncionario,
        undefined,
        undefined,
        undefined,
        undefined,
      );

      pushInfoNotification('Sucesso', 'Coleta de preços criada com sucesso!');

      if (idRequisicaoCompra) {
        if (openNewTab) {
          window.open(`/ColetaPrecos_cadastro?id=${idColetaPreco}`, '_blank', 'noopener,noreferrer');
        } else {
          backOrNavigate(`/ColetaPrecos_cadastro?id=${idColetaPreco}`, { replace: true });
        }
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao iniciar coleta de preços!'));
    }
  }, [
    idRequisicaoCompra,
    funcionarioInfo,
    createColetaPrecoUseCase,
    pushInfoNotification,
    pushErrorNotification,
    backOrNavigate,
  ]);

  const handleOpenChangeHistory = useCallback((
    idTabela: number | null,
    tipoTabela: LogTipoTabela | null,
  ) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (idRequisicaoCompra) {
        changeHistory.handleOpenChangeHistory(idRequisicaoCompra, LogTipoTabela.REQUISICAO_COMPRA, true);
      }
    }
  }, [
    idRequisicaoCompra,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    centroCustoDropdown: centroCustoDropdown ?? [],
    funcionariosAprovadosDropdown: funcionariosAprovadosDropdown ?? [],
    funcionariosSolicitanteDropdown: funcionariosSolicitanteDropdown ?? [],
    produtosServicosDropdown: produtosServicosDropdown ?? [],
    subcontasDropdown: subcontasDropdown ?? [],
    idRequisicaoCompra: idRequisicaoCompra,
    requisicaoCompra: requisicaoCompra ?? null,
    atributoCompra: atributoCompra ?? null,
    requisicaoServicosProdutos: requisicaoProdutosServicos ?? [],
    alteracaoRequisicaoProdutosServicos: alteracaoRequisicaoProdutosServicos ?? [],
    selectedRequisicaoProdutoServico: selectedRequisicaoProdutoServico ?? null,
    requisicaoProdutoServicoModal,
    requisicaoProdutoServicoDeleteModal,
    requisicaoCompraDeleteModal,
    lockSolicitante,
    buttonOptions,
    statusOptions,
    hookForm,
    pagination,
    status,
    enableColetaPreco,
    lockEdition,
    reportViewerState,
    changeHistory,
    centroCustoDrawerState,
    produtoServicoDrawerState,
    handleReload,
    handleBack,
    handleOptionPopperActions,
    handleSubmit: hookForm.handleSubmit(handleRequisicaoCompraSubmit),
    handleRequisicaoCompraDeleteSubmit,
    handleRequisicaoProdutoServicoSubmit,
    handleRequisicaoProdutoServicoDeleteSubmit,
    handleNewProdutoServico,
    handleStatusChangeSubmit,
    handleCreateCentroCusto,
    handleRequisicaoServicoProdutoPageChange,
    handleRequisicaoServicoProdutoRowsPerPageChange,
    handleAlteracaoRequisicaoServicoProdutoPageChange,
    handleAlteracaoRequisicaoServicoProdutoRowsPerPageChange,
    handleDeleteFile,
    handleDownloadFile,
    handleRequisicaoCompraDeleteOpenState,
    handleRequisicaoProdutoServicoOpenState,
    handleRequisicaoProdutoServicoDeleteOpenState,
    handleProdutoServicoOpenState,
    handleNovaColetaPrecos,
    handleOpenChangeHistory,
  };
}